import React, { useState, useCallback, useRef, useEffect } from 'react';
import { getCOSPresigned, uploadLKEFile as runAsync } from '../../../service/service';
import useCos from './useCOS';

import { UPLOAD_FILE_TYPE_LIST, UPLOAD_FILE_IMG_TYPE_LIST, FILE_STATUS_MAP } from '../../../config/constant';
import { notification } from 'tea-component';
// import { fileTypeFromBlob } from 'file-type';
// import { notification } from 'tea-component';
import { useIntl } from 'umi';
import { getPreviewName } from '../../../ChatPage/utils';
import { v4 as uuidv4 } from 'uuid';
import FormatHtmlTextToReact from '@home/components/FormatHtmlTextToReact';

interface ICOSConfig {
  region: string;
  bucket: string;
  session_token: string;
  tmp_secret_id: string;
  tmp_secret_key: string;
}

interface ICOSUrlConfig {
  url?: string[];
}

interface UploadItem {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress?: number;
  error?: string;
  fileSrc?: string;
  // 原始图片链接
  originalSrc?:string;
  name?: string;
  previewName?: string;
  type?: string;
  lastModified?: number;
}

interface UseUploadImgHooksProps {
  maxFileSize?: number;
  acceptedFormats?: string[];
  getCOSConfig?: () => Promise<ICOSConfig>;
  onUploadSuccess?: (fileSrc: string, file: File) => void;
  onUploadError?: (error: Error, file: File) => void;
  onBatchUploadStart?: (files: File[]) => void;
  onBatchUploadComplete?: (results: { success: number; error: number; }) => void;
  isCopilot: boolean;
  dropAreaRef?: React.MutableRefObject<HTMLDivElement | null>;
  initialHomeSessionId?: string;
}

const useUploadImgHooks = (props: UseUploadImgHooksProps) => {
  const {
    maxFileSize = 5,
    maxCount = 5,
    acceptedFormats = UPLOAD_FILE_TYPE_LIST,
    // getCOSConfig: propsGetCOSConfig,
    onUploadSuccess,
    onUploadError,
    onBatchUploadStart,
    onBatchUploadComplete,
    fileList,
    // isEdit,
    disableDragUpload,
    isCopilot,
    dropAreaRef,
    initialHomeSessionId: sessionId 
  } = props;
  const [uploadList, setUploadList] = useState<UploadItem[]>(fileList || []);
  const uploadListRef = useRef(uploadList);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const dragAreaElement = isCopilot ? dropAreaRef?.current : document.getElementById('root');
  const { formatMessage } = useIntl();

  // 同步 ref 和 state
  useEffect(() => {
    uploadListRef.current = uploadList;
  }, [uploadList]);


  const handleDragOver = useCallback((e: DragEvent) => {

    e.preventDefault();
    e.stopPropagation();
  }, [disableDragUpload]);

  const handleDragEnter = (e: DragEvent) => {
    if (disableDragUpload) return;
    // 新增判断：只处理文件拖拽，忽略文字选中拖拽
    if (!e.dataTransfer?.types.includes('Files')) return;

    // 关键判断：只有从外部（非root内部元素）拖入时才计数
    if (!e.relatedTarget || !dragAreaElement?.contains(e.relatedTarget as Node)) {
      dragCounter.current += 1;
      if (dragCounter.current === 1) {
        setIsDragging(true);
      }
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: DragEvent) => {
    if (disableDragUpload) return;
    // 关键判断：只有离开到外部（非root内部元素）时才减少计数
    if (!e.relatedTarget || !dragAreaElement?.contains(e.relatedTarget as Node)) {
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    }
    e.preventDefault();
    e.stopPropagation();
  };



  // 获取 COS 配置
  // const unifiedGetCOSConfig = useCallback(async () => propsGetCOSConfig ? await propsGetCOSConfig() : await getCOSConfig(), [propsGetCOSConfig]);

  const { putObject, putObjects } = useCos();

  // 更新上传项状态
  const updateUploadItem = useCallback((id: string, updates: Partial<UploadItem>) => {
    setUploadList(prev => prev.map(item =>
      item.id === id ? { ...item, ...updates } : item
    ));
  }, []);

  const getFileExtension = (filename: string) => filename?.split('.').pop()?.toLowerCase() || '';

  // 判断是否为图片类型
  const isImageFile = useCallback((filename: string) => {
    const extension = getFileExtension(filename);
    return UPLOAD_FILE_IMG_TYPE_LIST.includes(extension);
  }, []);


  // 文件验证
  const validateFile = useCallback((file: File) => {
    if (!file || file.size === 0) {
      return { valid: false, error: '文件无效或为空' };
    }
    const fileSizeInMB = file.size / (1024 * 1024);
    const fileExtension = getFileExtension(file.name);
    if (!acceptedFormats.includes(fileExtension)) {
      return { valid: false, error: `不支持的文件格式（支持: ${acceptedFormats.join(', ')}）` };
    }
    // 图片最大限制为5M
    if (isImageFile(file.name)) {
      if (fileSizeInMB > maxFileSize) {
        return { valid: false, error: `图片大小超过限制（最大 ${maxFileSize}MB）` };
      } 
    }
    // 这里如果是md文档,需要限制大小为1M ,其他文件最大为100M
    if(fileExtension === 'md'){
      if(fileSizeInMB >= 1){
        return { valid: false, error: `文件大小超过限制（最大 1MB）` };
      }
    }
    // 除图片外的文件,最大10M
    if (!isImageFile(file.name) && fileExtension !== 'md') {
      if (fileSizeInMB >= 10) {
        return { valid: false, error: `文件大小超过限制（最大 100MB）` };
      }
    }  

    return { valid: true };
  }, [isImageFile, acceptedFormats, maxFileSize]);

  // 添加上传项
  const addUploadItems = useCallback((files: File[]) => {
    const uploadedCount = uploadListRef?.current?.length || 0;
    if (uploadedCount >= maxCount) {
      notification.error({
        description: <FormatHtmlTextToReact text={formatMessage({ id: "dba_upload_img_error_tips" })} /> ,
        duration: 2000,
      });
      return [];
    }
    if (!files || files.length === 0) return [];
    const newItems = files.map(file => {
      const previewUrl = URL.createObjectURL(file);
      const validation = validateFile(file);
      const { name, size, lastModified } = file;
      const type = getFileExtension(name);
      // 使用文件特征生成唯一ID
      const id = `upload-${lastModified}-${size}-${Math.random().toString(36).substr(2, 6)}-${name}`;

      if (!validation.valid) {
        return {
          id,
          file,
          status: 'error' as const,
          error: validation.error,
          progress: 0,
          name,
          previewName: getPreviewName(name),
          size,
          type,
          lastModified,
          previewUrl
        };
      }

      return {
        id,
        file,
        status: 'pending' as const,
        progress: 0,
        name,
        previewName: getPreviewName(name),
        size,
        type,
        lastModified,
        previewUrl
      };
    });

    let newItemsList = newItems?.filter(item => !item.error);
    const errorFile = newItems?.find(item => item.error);
    if(errorFile) {
      notification.error({
        description: <FormatHtmlTextToReact text={formatMessage({ id: "dba_upload_img_error_tips" })} />,
        duration: 2000,
      });
    }
    const addCount = maxCount - uploadedCount;
    // 一次上传超过5个，则只上传有效文件的前5个
    newItemsList = newItemsList?.length > addCount ? newItemsList.slice(0, addCount) : newItemsList;

    setUploadList(prev => [...prev, ...newItemsList]);
    return newItemsList;
  }, [maxCount, formatMessage, validateFile]);

  // 上传单个文件
  const uploadSingleFile = useCallback(async (file: File, itemId: string) => {
    if (!file) {
      updateUploadItem(itemId, {
        status: 'error',
        error: '文件无效'
      });
      return { success: false };
    }

    const { name, size, lastModified } = file;
    // 重置上传项状态
    updateUploadItem(itemId, {
      status: 'uploading',
      progress: 0,
      name,
      // type,
      previewName: getPreviewName(name),
      size,
      lastModified,
      error: undefined,
    });

    try {
      const fileName = `${Date.now()}_${name.replace(/\s+/g, '-')}`;
      const filePath = `/agent/file/${fileName}`;
      const {protocol} = window.location;

      const result = await putObject({
        path: filePath,
        file,
        onProgress: (progressData: { percent: number; }) => {
          updateUploadItem(itemId, { progress: progressData.percent * 100 });
        },
      });

      const srcList = await getCOSPresigned({
        "cos_type": "agentCos",
        "url": [`${protocol}//${result?.Location}`]
      }) as ICOSUrlConfig;
 
      const fileSrc = srcList?.url?.[0] || '';
      const originalSrc = `${protocol}//${result?.Location}`;
      const type = getFileExtension(name);
      
      // 判断是否为图片，如果是图片直接标记为 success
      if (isImageFile(name)) {
        updateUploadItem(itemId, {
          status: 'success',
          fileSrc,
          originalSrc,
          size,
          name,
          previewName: getPreviewName(name),
          type,
          lastModified,
          progress: 100
        });
        onUploadSuccess?.(fileSrc, file);
        return { success: true, fileSrc, isImage: true, originalSrc, type, name };
      } 
      // 如果是文件，先不标记为 success，等待 runAsync 调用
      updateUploadItem(itemId, {
        status: 'uploading',
        fileSrc,
        originalSrc,
        size,
        name,
        previewName: getPreviewName(name),
        type,
        lastModified,
        progress: 100
      });
        
      // 调用 runAsync 上传文件信息
      try {
        const res =  await runAsync({
          "trace_id": uuidv4(),
          "session_id": sessionId,
          files: [{
            url: originalSrc,
            type,
            name,
            id: itemId,
          }]
        });
        if (res?.res_code !== 0) {
          updateUploadItem(itemId, {
            status: 'error'
          });
          onUploadSuccess?.(fileSrc, file);
          return { success: false, fileSrc,  error: res?.res_msg };
        }
        const fileItemStatus = res?.files?.find((task: { id: string; })=> task?.id === itemId);
        // runAsync 成功后标记为 success
        updateUploadItem(itemId, {
          status: FILE_STATUS_MAP?.[fileItemStatus?.status] || 'error'
        });
        onUploadSuccess?.(fileSrc, file);
        return { success: true, fileSrc, isImage: false, originalSrc, type, name };
      } catch (error) {
        console.error('runAsync 调用失败:', error);
        updateUploadItem(itemId, {
          status: 'error',
          error: 'runAsync 调用失败'
        });
        return { success: false, error: error as Error };
      }
      
    } catch (error) {
      console.error('上传失败:', error);
      updateUploadItem(itemId, {
        status: 'error',
        error: (error as Error).message || '上传失败'
      });
      onUploadError?.(error as Error, file);
      return { success: false, error: error as Error };
    }
  }, [putObject, onUploadSuccess, onUploadError, updateUploadItem]);

  // 批量上传文件
  const uploadBatchFiles = useCallback(async (fileItems: { file: File; itemId: string; }[]) => {
    if (!fileItems || fileItems.length === 0) {
      return { success: 0, error: 0 };
    }

    const {protocol} = window.location;
    const validItems = fileItems.filter(item => item.file);

    // 准备上传参数
    const uploadParams = validItems.map(({ file, itemId }) => {
      const { name } = file;
      const fileName = `${Date.now()}_${name.replace(/\s+/g, '-')}`;
      const filePath = `/agent/file/${fileName}`;

      return {
        path: filePath,
        file,
        onProgress: (progressData: { percent: number; }) => {
          updateUploadItem(itemId, { progress: progressData.percent * 100 });
        },
      };
    });

    try {
      const results = await putObjects(uploadParams);

      // 处理上传结果
      const successUploads = results
        .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
        .map(result => result.value);

      const locations = successUploads.map(result => `${protocol}//${result?.Location}`);

      // 批量获取预签名URL
      const srcList = await getCOSPresigned({
        "cos_type": "agentCos",
        "url": locations
      }) as ICOSUrlConfig;
      
      // 收集所有非图片文件信息
      const nonImageFiles: Array<{ url: string; type: string; name: string; }> = [];
      const imageFiles: Array<{ itemId: string; index: number; }> = [];
      // 先更新所有文件的基本信息
      fileItems.forEach(({ file, itemId }, index) => {
        const result = results[index];
        const { name, size, lastModified } = file;
        const type = getFileExtension(name);
        
        if (result?.status === 'fulfilled') {
          const fileSrc = srcList?.url?.[index] || '';
          const originalSrc = locations[index] || '';
          
          // 判断是否为图片
          if (isImageFile(name)) {
            // 图片直接标记为 success
            updateUploadItem(itemId, {
              status: 'success',
              fileSrc,
              originalSrc,
              size,
              name,
              previewName: getPreviewName(name),
              type,
              lastModified,
              progress: 100
            });
            onUploadSuccess?.(fileSrc, file);
            imageFiles.push({ itemId, index });
          } else {
            // 非图片文件先标记为 uploading
            updateUploadItem(itemId, {
              status: 'uploading',
              fileSrc,
              originalSrc,
              size,
              name,
              previewName: getPreviewName(name),
              type,
              lastModified,
              progress: 100
            });
            nonImageFiles.push({
              url: originalSrc,
              type,
              name,
              id: itemId,
            });
          }
        } else {
          const error = (result as PromiseRejectedResult)?.reason || new Error('上传失败');
          updateUploadItem(itemId, {
            status: 'error',
            error: error.message
          });
          onUploadError?.(error, file);
        }
      });
      
      // 如果有非图片文件，调用 runAsync
      if (nonImageFiles.length > 0) {
        try {
          const res = await runAsync({
            files: nonImageFiles,
            "trace_id": uuidv4(),
            "session_id": sessionId,
          });
          if (res?.res_code !== 0) {
            fileItems.forEach(({ file, itemId }, index) => {
              const result = results[index];
              if (result?.status === 'fulfilled' && !isImageFile(file.name)) {
                updateUploadItem(itemId, {
                  status: 'error',
                  error: 'runAsync 调用失败'
                });
              }
            });
            return {
              success: 0,
              error: fileItems.length
            };
          }
          // runAsync 成功后，将所有非图片文件标记为 success
          fileItems.forEach(({ file, itemId }, index) => {
            const result = results[index];
            const fileItemStatus = res?.files?.find((task: { id: string; })=> task?.id === itemId);
            if (result?.status === 'fulfilled' && !isImageFile(file.name)) {
              updateUploadItem(itemId, {
                status: FILE_STATUS_MAP?.[fileItemStatus?.status] || 'error',
              });
              const fileSrc = srcList?.url?.[index] || '';
              onUploadSuccess?.(fileSrc, file);
            }
          });
        } catch (error) {
          console.error('runAsync 调用失败:', error);
          // runAsync 失败，将所有非图片文件标记为 error
          fileItems.forEach(({ file, itemId }, index) => {
            const result = results[index];
            if (result?.status === 'fulfilled' && !isImageFile(file.name)) {
              updateUploadItem(itemId, {
                status: 'error',
                error: 'runAsync 调用失败'
              });
            }
          });
        }
      }

      return {
        success: successUploads.length,
        error: results.length - successUploads.length
      };
    } catch (error) {
      console.error('批量上传失败:', error);
      // 标记所有文件为错误状态
      fileItems.forEach(({ file, itemId }) => {
        updateUploadItem(itemId, {
          status: 'error',
          error: (error as Error)?.message || '批量上传失败'
        });
        onUploadError?.(error as Error, file);
      });
      return {
        success: 0,
        error: fileItems.length
      };
    }
  }, [putObjects, onUploadSuccess, onUploadError, updateUploadItem]);

  // 处理批量上传
  const batchUploadFiles = useCallback(async (files: File[]) => {
    if (!files || files.length === 0) {
      return;
    }

    onBatchUploadStart?.(files);

    const newItems = addUploadItems(files);
    const validItems = newItems.filter(item => item.status === 'pending');

    if (validItems.length === 0) {
      onBatchUploadComplete?.({ success: 0, error: files.length });
      return;
    }

    try {
      if (validItems.length === 1) {
        const result = await uploadSingleFile(validItems[0].file, validItems[0].id);
        onBatchUploadComplete?.({
          success: result.success ? 1 : 0,
          error: result.success ? 0 : 1
        });
      } else {
        const fileItems = validItems.map(item => ({
          file: item.file,
          itemId: item.id
        }));

        const result = await uploadBatchFiles(fileItems);
        onBatchUploadComplete?.(result);
      }
    } catch (error) {
      console.error('上传过程中出错:', error);
      onBatchUploadComplete?.({
        success: 0,
        error: validItems.length
      });
    }
  }, [addUploadItems, uploadSingleFile, uploadBatchFiles, onBatchUploadStart, onBatchUploadComplete]);

  // 处理文件放置
  const handleDrop = useCallback((e: DragEvent) => {
    if (disableDragUpload) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      batchUploadFiles(Array.from(files));
    }
  }, [batchUploadFiles, disableDragUpload]);
  
  // 处理文件上传
  const handleFileUpload = useCallback((data: File, from?: string) => {
    const file = data?.file;
    if (!file) {
      console.error('尝试上传无效文件');
      return;
    }

    // 重新上传逻辑
    if (from === 'reUpLoad') {
      // 使用更精确的匹配条件：ID + 文件名 + 大小 + 最后修改时间
      const existingItem = uploadListRef.current.find(item =>
        item.id === data.id &&
        item.file.name === file.name &&
        item.file.size === file.size &&
        item.file.type === file.type
        // item.file.lastModified === file.lastModified 
        // &&item.status !== 'uploading'
      );
      if (existingItem) {
        // 标记为上传中，防止重复上传
        updateUploadItem(existingItem.id, {
          status: 'uploading',
          progress: 0,
          error: undefined
        });
        uploadSingleFile(file, existingItem.id);
        return;
      }
    }

    // 新文件上传
    const newItems = addUploadItems([file]);
    const validItems = newItems.filter(item => item.status === 'pending');

    if (validItems.length > 0) {
      uploadSingleFile(validItems[0].file, validItems[0].id);
    }
  }, [uploadSingleFile, addUploadItems, updateUploadItem]);

  // 处理粘贴上传
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    if (disableDragUpload) return;
    // e.preventDefault();
    const { items } = e.clipboardData;
    const files: File[] = [];
    for (const item of items) {
      if (item.kind === "file") {
        const blob = item.getAsFile();
        if (blob) {
          // const typeVal = blob.name?.split('.').pop()?.toLowerCase() || 'file';
          const typeVal = getFileExtension(blob.name);
          let fileName = `${Date.now()}.${typeVal}`;
          if(!isImageFile(fileName)) {
            fileName = blob.name;
          }
          const file = new File([blob], fileName, {
            type: blob.type,
          });
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      batchUploadFiles(files);
    }
  }, [batchUploadFiles]);

  // 删除上传项
  const handleRemoveItem = useCallback((itemId: string) => {
    setUploadList(prev => prev.filter(item => item.id !== itemId));
  }, []);

  // 重新上传
  const handleReUpload = useCallback((itemId: string) => {
    const item = uploadListRef.current.find(i => i.id === itemId);
    if (item) {
      handleFileUpload(item, 'reUpLoad');
    }
  }, [handleFileUpload]);

  const handleClickUpload = useCallback(() => {
    // 创建隐藏的 file input 元素
    if (uploadListRef?.current?.length >= maxCount) {
      // notification.error({
      //   description: formatMessage({ id: "dba_upload_img_error_tips" }),
      //   duration: 2000,
      // });
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true; // 支持多选
    input.accept = acceptedFormats.map(format => `.${format}`).join(','); // 设置可接受文件类型

    // 文件选择回调
    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        batchUploadFiles(Array.from(target.files)); // 调用现有批量上传方法
      }
    };

    input.click(); // 自动触发文件选择窗口
  }, [batchUploadFiles, acceptedFormats]);

  // 添加事件监听器
  useEffect(() => {
    if (!dragAreaElement || disableDragUpload) return;

    dragAreaElement.addEventListener('dragenter', handleDragEnter);
    dragAreaElement.addEventListener('dragover', handleDragOver);
    dragAreaElement.addEventListener('dragleave', handleDragLeave);
    dragAreaElement.addEventListener('drop', handleDrop);

    // 清理函数：组件卸载时移除事件监听
    return () => {
      dragAreaElement.removeEventListener('dragenter', handleDragEnter);
      dragAreaElement.removeEventListener('dragover', handleDragOver);
      dragAreaElement.removeEventListener('dragleave', handleDragLeave);
      dragAreaElement.removeEventListener('drop', handleDrop);
    };
  }, [disableDragUpload, dragAreaElement, handleDrop]); // 空依赖数组表示只在组件挂载和卸载时执行

  // 获取不同状态的上传项
  const uploadingList = uploadList.filter(item => item.status === 'uploading');
  const pendingList = uploadList.filter(item => item.status === 'pending');
  const successList = uploadList.filter(item => item.status === 'success');
  const errorList = uploadList.filter(item => item.status === 'error');
  return {
    uploadList,
    uploadingList,
    successList,
    errorList,
    setUploadList,
    handlePaste,
    handleFileUpload,
    handleClickUpload,
    // handleDragUpload,
    // handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleRemoveItem,
    handleReUpload,
    batchUploadFiles,
    isDragging,
    imgUploadDisabled: uploadList.length >= maxCount,
    sendDisabled: errorList?.length > 0 || uploadingList?.length > 0 || pendingList?.length > 0,
  };
};

export default useUploadImgHooks;
